<script lang="ts" setup>
import type { GetCartItemsResponse } from '@vue-storefront/prestashop-api';
import VProductPrice from './VProductPrice.vue';
import { TrashIcon, Loader2Icon, AlertCircle } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { useCart } from '~/composables/useCart';
import { useCartItem } from '~/composables/useCartItem';
import { computed, ref } from 'vue';
import Alert from '@/components/ui/alert/Alert.vue';
import AlertDescription from '@/components/ui/alert/AlertDescription.vue';

type CartItems = GetCartItemsResponse['psdata']['products'];

const props = defineProps<{ product: CartItems[0] }>();

const localePath = useLocalePath();
const { t, locale } = useI18n();

const prod = computed(() => ({
  id_product: parseInt(props.product.id_product),
  id_product_attribute: props.product.id_product_attribute,
  quantity: props.product.quantity,
  allow_backorder: props.product.allow_backorder,
  availability:
    Number(props.product.quantity_available) > 0
      ? 'available'
      : props.product.allow_backorder
        ? 'backorder'
        : 'outofstock',
}));

// Check if this product is a preorder item
const isPreorderItem = computed(() => {
  return props.product?.allow_backorder
        && props.product?.quantity_available !== undefined
        && Number(props.product?.quantity_available) <= 0;
});

// Check if cart quantity exceeds available stock (backorder)
const isBackorderItem = computed(() => {
  const cartQty = Number(props.product?.cart_quantity || 0);
  const availableQty = Number(props.product?.quantity_available || 0);

  return props.product?.allow_backorder
        && cartQty > availableQty
        && availableQty > 0;
});

const { isAvailable, quantityInCart, quantitySelectable } = useCartItem(prod);
const { query: cartQuery, mutationUpdate, mutationRemove, isLoading: loading, removeFromCart, addToCart, isUpdating } = useCart();

const link = (product: CartItems[0]) => localePath(`/${product.id_product}-${product.link_rewrite}.html`);

const hasError = ref(false);

function incrementQuantity() {
  addToCart({
    ...prod.value,
    id_product_attribute: Number(prod.value.id_product_attribute),
    qty: 1,
    op: 'up',
  });
}

function decrementQuantity() {
  addToCart({
    ...prod.value,
    id_product_attribute: Number(prod.value.id_product_attribute),
    qty: 1,
    op: 'down',
  });
}

function handleRemoveFromCart() {
  removeFromCart({
    ...prod.value,
    id_product: prod.value.id_product,
    id_product_attribute: Number(prod.value.id_product_attribute),
  });
}
</script>

<template>
  <div>
    <div class="p-2 bg-white border rounded shadow flex flex-col gap-2 relative"
    :class="{'opacity-50 pointer-events-none': isUpdating}"
    >
      <span v-if="isUpdating" class="animate-spin absolute top-2 right-2">
        <Loader2Icon :size="20" />
      </span>

      <div class="grid grid-cols-[80px_1fr] md:grid-cols-[120px_1fr] gap-2 overflow-hidden">
        <div>
          <NuxtImg
            class="h-full w-auto max-h-20 md:max-h-30 object-cover"
            loading="lazy"
            :src="hasError ? '/fallback.jpg' : product.image_url"
            format="webp"
            :alt="product.name"
            @error="hasError = true"
          />
        </div>
        <div class="flex flex-col justify-start items-start w-full shrink-0 gap-2">
          <NuxtLink :to="link(product)">
            <p class="font-medium text-sm md:text-base line-clamp-2">{{ product.name }}</p>
          </NuxtLink>
          <div class="text-xs text-muted-foreground">{{ product.attributes }}</div>
          <VProductPrice
            :prices-data="{
              specific_prices: product.specific_prices,
              price: product.formatted_price,
              regular_price: product.formatted_regular_price,
              discount_percentage: product.formatted_discount_percentage
            }"
            size="sm"
          />
          
          <!-- Preorder Notice -->
          <Alert v-if="isPreorderItem" variant="default" class="bg-orange-50 border-orange-200">
            <AlertCircle class="w-4 h-4 text-orange-600" />
            <AlertDescription class="text-orange-800">
              {{ t('preorder_confirmation.alert') }}
            </AlertDescription>
          </Alert>
          
          <!-- Backorder Notice -->
          <Alert v-if="isBackorderItem" variant="default" class="bg-orange-50 border-orange-200">
            <AlertCircle class="w-4 h-4 text-orange-600" />
            <AlertDescription class="text-orange-800">
              <div class="space-y-2">
                <div>{{ t('backorder_confirmation.content.line1') }}</div>
                <div>{{ t('backorder_confirmation.content.line2') }}</div>
                <div class="text-sm">
                  {{ t('backorder_confirmation.content.line3') }}
                  <a :href="`https://shop.pogliani.com/${locale}/termini-e-condizioni`" 
                     class="underline hover:no-underline ml-1" 
                     target="_blank" 
                     rel="noopener">{{ t('Terms of Sale') }}</a>
                  {{ t('and') }}
                  <a :href="`https://shop.pogliani.com/${locale}/faqs-2`" 
                     class="underline hover:no-underline ml-1" 
                     target="_blank" 
                     rel="noopener">{{ t('FAQ') }}</a>
                </div>
              </div>
            </AlertDescription>
          </Alert>
          
        </div>
      </div>

      <div class="grid grid-cols-[80px_1fr] md:grid-cols-[120px_1fr] gap-2 overflow-hidden">
        <div class="gap-1 flex items-center justify-start w-full">
          <Button
            :disabled="quantityInCart == 1"
            variant="secondary"
            class="w-full text-base"
            size="sm"
            @click="decrementQuantity"
          >
            <span>&minus;</span>
          </Button>
          <Button
            variant="secondary"
            class="w-full text-base"
            size="sm"
            :disabled="!isAvailable"
            @click="incrementQuantity"
          >
            <span>&plus;</span>
          </Button>
        </div>
        <div class="flex items-center justify-between gap-1 text-sm">
          <div class="flex items-start gap-1">
            <span class="whitespace-nowrap text-lg">&times; {{ product.cart_quantity }}</span>
            <span class="ml-3 text-base">{{ t('Total') }}: <span class="font-semibold">{{ product.formatted_total }}</span></span>
          </div>
          <div class="flex items-end gap-2">
            <div class="flex gap-2 justify-end w-full flex-1">
              <Button
                size="sm"
                variant="outline"
                class="hover:text-red-600"
                :disabled="isUpdating"
                @click="handleRemoveFromCart"
              >
                <TrashIcon :size="20" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
